'use client';

import { useSyncExternalStore } from 'react';

// The build version baked into THIS bundle at image build (CI passes vNNN). Empty in local dev.
const OWN_SHA = process.env.NEXT_PUBLIC_BUILD_SHA ?? '';
const POLL_MS = 60_000;

/**
 * Deploy-reach for long-lived sessions (the TMA SPA never re-fetches its HTML shell): a
 * single module-level poller hits `/build-version` (same Next app; nginx routes non-/api to
 * it on every host) and flips `stale` the moment the server's build version differs from the one
 * baked into this bundle. It renders NOTHING on its own — the ONLY surface is the Rush Mid
 * lobby, which shows an "update" button and refuses to start a match on a stale bundle (a
 * mid-match session is unaffected; the server is authoritative and resume-on-open drops the
 * player back into their live round). Old bundles keep ignoring unknown snapshot events until
 * the user updates (forward-compat rule in docs/rush_mid.md §4).
 */
let stale = false;
const listeners = new Set<() => void>();
let started = false;

function startPolling(): void {
  if (started || typeof window === 'undefined') return;
  if (!OWN_SHA || OWN_SHA === 'dev') return; // local dev / storybook — nothing to compare
  started = true;
  const check = async (): Promise<void> => {
    try {
      const res = await fetch('/build-version', { cache: 'no-store' });
      if (!res.ok) return;
      const { sha } = (await res.json()) as { sha?: string };
      if (!stale && sha && sha !== 'dev' && sha !== OWN_SHA) {
        stale = true;
        for (const l of listeners) l();
      }
    } catch {
      // offline / mid-deploy blip — the next tick retries
    }
  };
  window.setInterval(check, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  void check();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  startPolling();
  return () => listeners.delete(cb);
}

/** True once a newer frontend build is live on the server than the one this bundle was built
 *  from. Consumed only by the Rush Mid lobby to gate match start behind an update. */
export function useBuildStale(): boolean {
  return useSyncExternalStore(subscribe, () => stale, () => false);
}
